/**
 * Phone Number data models with Zod schemas
 * Explicit schema definitions with parsing validation
 */
import { parseISO } from "date-fns";
import { z } from "zod";

// Domain schema (camelCase)
const PhoneNumberSchema = z.object({
  id: z.string(),
  createdAt: z.date(),
  modifiedAt: z.date(),
  businessId: z.string(),
  number: z.string(),
  twilioSid: z.string(),
  status: z.enum(["active", "inactive"]),
  deletedAt: z.date().nullable(),
  createdBy: z.string().nullable(),
});

const CreatePhoneNumberRequestSchema = PhoneNumberSchema.omit({
  id: true,
  createdAt: true,
  modifiedAt: true,
  deletedAt: true,
}).partial({
  status: true,
  createdBy: true,
});

const UpdatePhoneNumberRequestSchema = PhoneNumberSchema.omit({
  id: true,
  createdAt: true,
  modifiedAt: true,
  businessId: true,
  deletedAt: true,
  createdBy: true,
}).partial();

// DTO schema (snake_case)
const PhoneNumberDtoSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  modified_at: z.string(),
  business_id: z.string(),
  number: z.string(),
  twilio_sid: z.string(),
  status: z.enum(["active", "inactive"]),
  deleted_at: z.string().nullable(),
  created_by: z.string().nullable(),
});

export const CreatePhoneNumberDtoSchema = PhoneNumberDtoSchema.omit({
  id: true,
  created_at: true,
  modified_at: true,
  deleted_at: true,
}).partial({
  status: true,
  created_by: true,
});

export const UpdatePhoneNumberDtoSchema = PhoneNumberDtoSchema.omit({
  id: true,
  created_at: true,
  modified_at: true,
  business_id: true,
  deleted_at: true,
  created_by: true,
}).partial();

// Infer DTO types
export type PhoneNumberDto = z.infer<typeof PhoneNumberDtoSchema>;
export type CreatePhoneNumberDto = z.infer<typeof CreatePhoneNumberDtoSchema>;
export type UpdatePhoneNumberDto = z.infer<typeof UpdatePhoneNumberDtoSchema>;

// Infer domain types
export type PhoneNumber = z.infer<typeof PhoneNumberSchema>;
export type CreatePhoneNumberRequest = z.infer<
  typeof CreatePhoneNumberRequestSchema
>;
export type UpdatePhoneNumberRequest = z.infer<
  typeof UpdatePhoneNumberRequestSchema
>;

// ============================================================================
// CONVERSION UTILITIES
// ============================================================================

export function fromPhoneNumberDtoToPhoneNumber(
  dto: PhoneNumberDto
): PhoneNumber {
  const phoneNumberDto = PhoneNumberDtoSchema.parse(dto);

  return {
    id: phoneNumberDto.id,
    createdAt: parseISO(phoneNumberDto.created_at),
    modifiedAt: parseISO(phoneNumberDto.modified_at),
    businessId: phoneNumberDto.business_id,
    number: phoneNumberDto.number,
    twilioSid: phoneNumberDto.twilio_sid,
    status: phoneNumberDto.status,
    deletedAt: phoneNumberDto.deleted_at
      ? parseISO(phoneNumberDto.deleted_at)
      : null,
    createdBy: phoneNumberDto.created_by,
  };
}

export function fromCreatePhoneNumberRequestToCreatePhoneNumberDto(
  request: CreatePhoneNumberRequest
): CreatePhoneNumberDto {
  const createPhoneNumberRequest =
    CreatePhoneNumberRequestSchema.parse(request);

  return {
    business_id: createPhoneNumberRequest.businessId,
    number: createPhoneNumberRequest.number,
    twilio_sid: createPhoneNumberRequest.twilioSid,
    status: createPhoneNumberRequest.status,
    created_by: createPhoneNumberRequest.createdBy,
  };
}

export function fromUpdatePhoneNumberRequestToUpdatePhoneNumberDto(
  request: UpdatePhoneNumberRequest
): UpdatePhoneNumberDto {
  const updatePhoneNumberRequest =
    UpdatePhoneNumberRequestSchema.parse(request);

  return {
    number: updatePhoneNumberRequest.number,
    twilio_sid: updatePhoneNumberRequest.twilioSid,
    status: updatePhoneNumberRequest.status,
  };
}
